import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteChangeOrderBudgetLineItem,
  deleteChangeOrderDeliverable,
  deleteChangeOrderMilestones,
  getBudgetChangeOrderDetails,
  getDeliverableChangeOrderDetails,
  getMilestoneChangeOrderDetails,
  type MilestoneChangeOrderDetailsVariables,
} from '@/api/Sherpa/ChangeOrder/index';
import { CACHE_TIME } from '@/constants';
import { SherpaProjectReactQueryKey } from '@/constants/ReactQueryKeys';
import { useAppDispatch } from '@/store';
import { notifyUser } from '@/store/Toast';
import { sanitizeResponseError } from '@/utils';

export const useMilestoneChangeOrderDetails = ({ id }: MilestoneChangeOrderDetailsVariables) => {
  //Tanstack: version 5 query implementation
  return useQuery({
    queryKey: [SherpaProjectReactQueryKey.GET_CHANGE_ORDER_MILESTONES, id],
    queryFn: () => getMilestoneChangeOrderDetails({ id }),
    staleTime: CACHE_TIME,
    cacheTime: CACHE_TIME,
  });
};

export const useDeliverableChangeOrderDetails = ({ id }: MilestoneChangeOrderDetailsVariables) => {
  //Tanstack: version 5 query implementation
  return useQuery({
    queryKey: [SherpaProjectReactQueryKey.GET_CHANGE_ORDER_DELIVERABLES, id],
    queryFn: () => getDeliverableChangeOrderDetails({ id }),
    staleTime: CACHE_TIME,
    cacheTime: CACHE_TIME,
  });
};

export const useBudgetChangeOrderDetails = ({ id }: MilestoneChangeOrderDetailsVariables) => {
  //Tanstack: version 5 query implementation
  return useQuery({
    queryKey: [SherpaProjectReactQueryKey.GET_CHANGE_ORDER_BUDGET, id],
    queryFn: () => getBudgetChangeOrderDetails({ id }),
    staleTime: CACHE_TIME,
    cacheTime: CACHE_TIME,
  });
};

export const useDeleteMilestoneChangeOrder = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  return useMutation((changeOrderId: number) => deleteChangeOrderMilestones({ changeOrderId }), {
    onSuccess: () => {
      //Tanstack: Invalidate queries after mutation
      queryClient.invalidateQueries({ queryKey: [SherpaProjectReactQueryKey.GET_CHANGE_ORDER_MILESTONES] });
      queryClient.invalidateQueries({ queryKey: [SherpaProjectReactQueryKey.GET_CHANGE_ORDER_SUMMARY] });
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Milestone change order deleted successfully',
          toastType: 'success',
        })
      );
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });
};
export const useDeleteDeliverableChangeOrder = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  return useMutation((changeOrderId: number) => deleteChangeOrderDeliverable({ changeOrderId }), {
    onSuccess: () => {
      //Tanstack: Invalidate queries after mutation
      queryClient.invalidateQueries({ queryKey: [SherpaProjectReactQueryKey.GET_CHANGE_ORDER_DELIVERABLES] });
      queryClient.invalidateQueries({ queryKey: [SherpaProjectReactQueryKey.GET_CHANGE_ORDER_SUMMARY] });
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Deliverable change order deleted successfully',
          toastType: 'success',
        })
      );
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });
};
export const useDeleteBudgetChangeOrder = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  return useMutation((changeOrderId: number) => deleteChangeOrderBudgetLineItem({ changeOrderId }), {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SherpaProjectReactQueryKey.GET_CHANGE_ORDER_BUDGET] });
      queryClient.invalidateQueries({ queryKey: [SherpaProjectReactQueryKey.GET_CHANGE_ORDER_SUMMARY] });
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Budget line item change order deleted successfully',
          toastType: 'success',
        })
      );
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });
};
